import { Link } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import SeoMeta from "@/components/feature/SeoMeta";

const faqGroups = [
  {
    title: "关于内容",
    items: [
      {
        q: "吃瓜视频合集多久更新一次？",
        a: "我们的内容团队会全天候跟踪全网热点事件，热门事件发生后通常会在数小时内整理完成并更新到对应分类。每日固定更新至少一次，重大事件会优先加急上线，确保你看到的第一手新鲜瓜。",
      },
      {
        q: "视频内容来源可靠吗？",
        a: "平台内容均来自公开渠道并经过团队筛选核实。我们会梳理事件完整脉络，避免断章取义，让每一位观众都能理性、客观地了解事情全貌。",
      },
      {
        q: "如何快速找到想看的热门视频？",
        a: "你可以查看首页的「热门推荐」与「排行榜」，或使用顶部搜索框输入关键词精准定位。也可以通过导航栏的分类入口，按明星八卦、综艺名场面、热搜盘点等方向浏览。",
      },
    ],
  },
  {
    title: "观看与体验",
    items: [
      {
        q: "支持哪些设备观看？",
        a: "网站采用响应式设计，支持电脑、平板和手机等多端自适应播放。无论使用什么设备，都能获得流畅清晰的观看体验，视频均提供高清清晰度。",
      },
      {
        q: "观看视频需要付费或注册吗？",
        a: "完全不需要。平台全部内容免费观看，无需注册登录即可直接播放。我们希望通过免费优质的国产吃瓜视频合集与精选，成为你日常吃瓜的首选站点。",
      },
      {
        q: "可以分享或收藏喜欢的视频吗？",
        a: "可以的。每个视频详情页都提供分享入口，你可以将喜欢的内容分享给朋友；建议同时加入浏览器书签，方便随时回来查看追更的内容。",
      },
    ],
  },
  {
    title: "合作与帮助",
    items: [
      {
        q: "如何参与投稿或内容合作？",
        a: "如果你有独家、有趣且符合平台定位的视频素材，欢迎通过页脚邮箱与我们联系。内容审核通过后将优先推荐曝光，我们期待与更多优质创作者合作。",
      },
      {
        q: "网站遇到问题怎么办？",
        a: "如果遇到页面无法打开、视频无法播放等问题，可以先尝试刷新页面或更换浏览器。若问题依旧存在，请将具体情况通过邮箱反馈给我们，我们会尽快排查处理。",
      },
      {
        q: "如何联系我们？",
        a: "可以通过页脚的邮箱与我们取得联系，我们会在工作日 9:00 - 18:00 内尽快回复。合作、投稿、反馈与建议都欢迎随时来信。",
      },
    ],
  },
];

const faqLd = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqGroups.flatMap((g) =>
    g.items.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  ),
};

export default function FaqPage() {
  return (
    <div className="bg-background-50 min-h-screen">
      <SeoMeta
        title="常见问题 - 国产吃瓜视频"
        description="国产吃瓜视频平台常见问题解答：内容更新频率、观看设备、是否免费、投稿合作与联系方式等，帮你快速了解吃瓜视频合集与精选平台的各项使用问题。"
        type="website"
        jsonLd={faqLd}
      />
      <SiteHeader />

      <section className="bg-secondary-100 py-16 md:py-24">
        <div className="mx-auto max-w-4xl px-4 md:px-6 text-center">
          <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-bold">
            <i className="ri-question-line"></i> 帮助中心
          </span>
          <h1 className="mt-4 font-heading text-3xl md:text-4xl font-black text-foreground-950">
            常见<span className="text-primary-600">问题</span>
          </h1>
          <p className="mt-3 text-foreground-600">
            关于国产吃瓜视频平台的一切疑问，都可以在这里找到答案。如仍无法解决，欢迎随时联系我们。
          </p>
        </div>
      </section>

      <div className="mx-auto max-w-4xl px-4 md:px-6 py-12 md:py-16 space-y-10">
        {faqGroups.map((group) => (
          <section key={group.title}>
            <h2 className="font-heading text-xl md:text-2xl font-black text-foreground-950 mb-4 flex items-center gap-2">
              <i className="ri-question-answer-line text-primary-500"></i>
              {group.title}
            </h2>
            <div className="space-y-3">
              {group.items.map((f, i) => (
                <details key={i} className="group bg-background-50 border border-background-200 rounded-xl overflow-hidden">
                  <summary className="flex items-center justify-between gap-3 cursor-pointer p-5 list-none hover:bg-background-100/60 transition-colors">
                    <span className="font-bold text-foreground-900 text-sm md:text-base">{f.q}</span>
                    <i className="ri-add-line text-primary-500 group-open:rotate-45 transition-transform"></i>
                  </summary>
                  <p className="px-5 pb-5 text-sm text-foreground-600 leading-relaxed">{f.a}</p>
                </details>
              ))}
            </div>
          </section>
        ))}

        <div className="bg-background-100 rounded-2xl p-8 text-center">
          <h2 className="font-heading text-xl md:text-2xl font-black text-foreground-950">还有疑问？</h2>
          <p className="mt-2 text-sm text-foreground-600">没找到想要的答案，欢迎联系我们，我们会尽快为你解答。</p>
          <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3">
            <a
              href="mailto:contact@guavideo.example"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-background-50 font-bold whitespace-nowrap hover:bg-primary-600 transition-colors"
            >
              <i className="ri-mail-line"></i> 联系我们
            </a>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-background-50 text-foreground-700 font-bold whitespace-nowrap hover:bg-background-200 transition-colors"
            >
              <i className="ri-home-line"></i> 返回首页
            </Link>
          </div>
        </div>
      </div>

      <SiteFooter />
    </div>
  );
}